'use client';

import { useState, useRef, useEffect } from 'react';
import { FiUsers, FiChevronDown } from 'react-icons/fi';
import { useTourStore } from '@eliadmin/core/src/stores/tours-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const TourFilterGuests = () => {
  const { filter, setFilter } = useTourStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslations();

  // Local state for adults and children
  const [guests, setGuests] = useState({
    adults: filter.adults || 2,
    children: filter.children || 0,
  });

  const handleGuestsChange = (type: 'adults' | 'children', value: number) => {
    setGuests(prev => ({
      ...prev,
      [type]: Math.max(0, value), // Prevent negative values
    }));
  };

  const handleApply = () => {
    setFilter({
      ...filter,
      adults: guests.adults,
      children: guests.children,
    });
    setIsOpen(false);
  };

  const getChildrenLabel = () => {
    if (guests.children === 1) {
      return t('text1837');
    } else if (guests.children >= 2 && guests.children <= 4) {
      return t('text1838');
    } else {
      return t('text1839');
    }
  };

  const getAdultsLabel = () => {
    if (guests.adults === 1) {
      return t('text1840');
    } else if (guests.adults >= 2 && guests.adults <= 4) {
      return t('text1841');
    } else {
      return t('text1842');
    }
  };

  const getSelectedLabel = () => {
    return `${guests.adults} ${getAdultsLabel()}${
      guests.children > 0 ? `, ${guests.children} ${getChildrenLabel()}` : ''
    }`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      {/* Main Dropdown Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-[30px] min-w-[180px] cursor-pointer items-center justify-between rounded-[8px] bg-white px-3 sm:h-[33px]"
      >
        <div className="flex items-center space-x-1">
          <FiUsers className="mr-1 h-4 w-4 text-gray-700" />
          <span className="whitespace-nowrap text-sm font-normal text-gray-800">
            {getSelectedLabel()}
          </span>
        </div>
        <FiChevronDown className="ml-2 h-4 w-4 text-gray-500 text-primary" />
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="p-4">
            {/* Adults Selector */}
            <div className="flex items-center justify-between py-2">
              <span className="text-xs font-medium text-gray-700 sm:text-sm">
                {t('text1841').charAt(0).toUpperCase() + t('text1841').slice(1)}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleGuestsChange('adults', guests.adults - 1)}
                  className="h-6 w-6 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  -
                </button>
                <span className="w-3 text-center text-sm font-medium text-gray-700">
                  {guests.adults}
                </span>
                <button
                  onClick={() => handleGuestsChange('adults', guests.adults + 1)}
                  className="h-6 w-6 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  +
                </button>
              </div>
            </div>

            {/* Children Selector */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-700">
                {t('text1838').charAt(0).toUpperCase() + t('text1838').slice(1)}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleGuestsChange('children', guests.children - 1)}
                  className="h-6 w-6 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  -
                </button>
                <span className="w-3 text-center text-sm font-medium text-gray-700">
                  {guests.children}
                </span>
                <button
                  onClick={() => handleGuestsChange('children', guests.children + 1)}
                  className="h-6 w-6 rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
